import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export const SafetyTab = ({ issue, setIssue }) => {
  const [checking, setChecking] = useState(false);
  const [report, setReport] = useState(null);

  const run = async () => {
    setChecking(true);
    try {
      const r = await api.post(`/admin/issues/${issue.id}/action`, { action: "run_safety_check" });
      setReport(r.data.safety);
      setIssue(r.data.issue);
      toast.success("Safety check complete");
    } catch (e) {
      toast.error("Safety check failed", { description: e?.response?.data?.detail || "" });
    } finally { setChecking(false); }
  };

  const currentStatus = issue.safety_status;
  const displayIssues = report?.issues || issue.safety_notes || [];

  const badgeCls = currentStatus === "passed" ? "bg-[color:var(--coh-success)] text-white"
    : currentStatus === "warning" ? "bg-[color:var(--coh-warning)] text-white"
    : currentStatus === "failed" ? "bg-destructive text-white"
    : "bg-secondary";

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="kicker">Editorial safety</div>
            <div className="mt-2 flex items-center gap-2">
              <Badge className={badgeCls}>{currentStatus || "pending"}</Badge>
              {currentStatus === "failed" && <span className="text-sm text-destructive">Approval blocked</span>}
            </div>
          </div>
          <Button onClick={run} disabled={checking} data-testid="safety-run-button" className="bg-[color:var(--coh-ink)] text-white hover:bg-[color:var(--coh-ink-2)]">
            <ShieldCheck className="h-4 w-4 mr-2" /> {checking ? "Running…" : "Run safety check"}
          </Button>
        </div>
      </Card>

      <Card className="p-6">
        <div className="kicker">Findings</div>
        {displayIssues.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">No findings yet. Run a check to see the report.</p>
        ) : (
          <ul className="mt-4 space-y-3">
            {displayIssues.map((it, idx) => (
              <li key={idx} className="p-4 border border-border rounded-lg bg-card">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={it.severity === "high" ? "border-destructive text-destructive" : it.severity === "medium" ? "border-[color:var(--coh-warning)] text-[color:var(--coh-warning)]" : ""}>
                    {it.severity}
                  </Badge>
                  <div className="text-xs text-muted-foreground">{it.section}</div>
                </div>
                <div className="mt-2 text-sm"><strong>Problem:</strong> {it.problem}</div>
                <div className="mt-1 text-sm"><strong>Fix:</strong> {it.recommended_fix}</div>
              </li>
            ))}
          </ul>
        )}
        {report?.approval_blockers?.length > 0 && (
          <div className="mt-4 p-4 border border-destructive/50 rounded-lg bg-destructive/5">
            <div className="kicker text-destructive">Approval blockers</div>
            <ul className="mt-2 list-disc list-inside text-sm">
              {report.approval_blockers.map((b, i) => <li key={i}>{b}</li>)}
            </ul>
          </div>
        )}
      </Card>
    </div>
  );
};
